<template>
    <!--
        Agencies list — TAG portal refresh, variant A.
        Desktop (≥768px): TopNav + AgenciesHeader (with facet chips +
            More filters trigger) + AgenciesTable + slide-in FilterRail.
        Mobile (<768px): AgenciesMobile takes over — top app bar, sticky
            search + status chips, card-row list, bottom tab bar, and a
            slide-up filter bottom-sheet (variant A · standard).
    -->
    <AgenciesMobile
        v-if="isMobile"
        :items="items"
        :loading="loading"
        :total-items="total_items"
        :search-value="searchRequest"
        :selected-statuses="filters.agencies_status"
        :status-options="formattedStatusOptions"
        :filters="filters"
        :options="options"
        :active-filter-count="activeFilterCount"
        @search="onSearchInput"
        @status-click="onStatusClick"
        @update-filter="onMobileFilterUpdate"
        @clear-filters="clearFilter"
    />

    <div v-else class="tag-app" :style="appStyle">
        <TopNav active-id="agencies" />

        <div :style="bodyStyle">
            <main :style="mainStyle">
                <AgenciesHeader
                    :total-items="total_items"
                    :search-value="searchRequest"
                    :selected-statuses="filters.agencies_status"
                    :status-options="formattedStatusOptions"
                    :export-href="exportHref"
                    :variant="'top'"
                    :facets="facetChips"
                    :active-filter-count="activeFilterCount"
                    @search="onSearchInput"
                    @status-click="onStatusClick"
                    @edit-columns="openColumnsModal"
                    @toggle-filters="toggleFilters"
                    @clear-facet="onClearFacet"
                />

                <div v-if="loading" :style="loaderWrapStyle">
                    <ClipLoader :loading="loading" color="#ec4657" size="36px" />
                </div>

                <AgenciesTable
                    v-else
                    :items="items"
                    :columns="current_columns"
                    :last-sort="lastSort"
                    :descending="descending"
                    @sort="sortData"
                    @delete="handleDeleteAgency"
                />
            </main>

            <!-- FilterRail as a right-side sheet. Hidden by default —
                 user opens via the "More filters" button or by clicking
                 a facet chip in the header. -->
            <FilterRail
                v-if="showFilters"
                :filters="filters"
                :options="options"
                :side="'right'"
                :closable="true"
                :width="280"
                @update:filters="onFiltersChanged"
                @clear="clearFilter"
                @close="showFilters = false"
            />
        </div>

        <Column_Modal
            v-if="showModalColumns"
            :is_show="showModalColumns"
            :__columns="current_columns"
            :selectColumn="selectColumn"
            :closeModal="closeModal"
            :selected_col="selected_col"
        />

        <Confirm_Modal
            v-if="delete_id"
            :title="getAgencyName"
            :onApplyAction="() => deleteAgency(delete_id)"
            :onSecondaryAction="() => handleDeleteAgency(null)"
            :applyLabel="'Delete'"
            :secondaryLabel="'Cancel'"
        />
    </div>
</template>

<script>
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import ClipLoader from "vue-spinner/src/ClipLoader.vue";
import axios from "axios";
import debounce from "lodash/debounce";

import Column_Modal from "../ColumnModal/index.vue";
import Confirm_Modal from "../ConfirmModal/index.vue";
import {
    sortDate,
    sortMoney,
    sortNumber,
    sortText,
} from "../../parts/sort-array";

import TopNav from "./_atoms/TopNav.vue";
import FilterRail from "./_atoms/FilterRail.vue";
import AgenciesHeader from "./_atoms/AgenciesHeader.vue";
import AgenciesTable from "./_atoms/AgenciesTable.vue";
import AgenciesMobile from "./_atoms/AgenciesMobile.vue";
import viewportMixin from "./_atoms/useViewport";

/**
 * Agencies list (variant A of the May-2026 "TAG portal — refresh" handoff).
 *
 * Layout
 *   - TopNav (horizontal, dark) at the top spans the full width.
 *   - Below it: main column with AgenciesHeader + AgenciesTable.
 *   - FilterRail slides in from the right as a sheet when the user
 *     clicks "More filters" or any facet chip in the header.
 *
 * Behaviour preserved
 *   - fetchData / sortData / clearFilter / deleteAgency / openColumnsModal /
 *     closeModal keep their pre-refresh shapes so the backend contract
 *     (`/agencies/getList`, `/agencies/updateColumns`, `/agencies/delete`)
 *     and the Column_Modal / Confirm_Modal integrations don't break.
 *
 * Dropped from legacy
 *   - "All/Any" radios beside Service/Industry — the FilterRail uses
 *     multi-select treeselect with OR semantics by default.
 *   - City / State / Distance filters — not in the mockup. Fields stay
 *     on `filters` as null so the backend isn't surprised.
 *   - Status checkboxes — replaced by the segmented control in
 *     AgenciesHeader.
 *
 * Filter changes are debounced 250ms via debouncedFetch — keystrokes
 * don't fire fetchData per character.
 */
export default {
    name: "Agencies",
    props: [
        "total_items",
        "gates",
        "columns",
        "industries",
        "services",
        "clientSizes",
        "languages",
        "niches",
        "skills",
        "sources",
        "states",
        "agency_statuses",
        "types",
        "states",
        // URL the backend hands us when the viewer is Portal admin; the
        // header shows the Export Excel button only when this is set.
        "exportHref",
    ],
    mixins: [viewportMixin],
    components: {
        ClipLoader,
        Column_Modal,
        Treeselect,
        Confirm_Modal,
        TopNav,
        FilterRail,
        AgenciesHeader,
        AgenciesTable,
        AgenciesMobile,
    },
    data() {
        return {
            selectedStatus: [],
            loading: false,
            items: [],
            filterItems: [],
            searchRequest: "",
            //selectedAgency: { id: 0, name: "All" },
            descending: false,
            lastSort: "active_deals",
            showModalColumns: false,
            selected_col: null,
            current_columns: [],
            filter_columns: [],
            is_open_sidebar: false,
            // Variant A: FilterRail is a sheet, off by default. Toggled
            // by the "More filters" button or by clicking a facet chip.
            showFilters: false,
            filters: {
                industry: null,
                service: null,
                name_in_quickbooks: "",
                client_size: null,
                international: null,
                type: null,
                language: null,
                niche: null,
                skill: null,
                source: null,
                min_budget: "",
                status: null,
                city: "",
                state: null,
                distance: null,
                enable_distance: false,
                agencies_status: [],
                budget_type: "monthly",
                services_is_all: true,
                industries_is_all: true,
                state: null,
            },
            options: {
                industry: this.industries,
                service: this.services,
                client_size: this.clientSizes,
                international: [
                    { id: true, label: "Yes" },
                    { id: false, label: "No" },
                ],
                type: this.types,
                language: this.languages,
                niche: this.niches,
                skill: this.skills,
                source: this.sources,
                state: this.states,
                status: this.agency_statuses || [],
                default: [
                    { id: "ab", label: "ab" },
                    { id: "ba", label: "ba" },
                ],
                states: this.states || [],
            },
            delete_id: null,
        };
    },

    mounted() {
        this.current_columns = JSON.parse(JSON.stringify(this.columns));
        this.debouncedFetch = debounce(this.fetchData, 250);
        this.fetchData();
    },

    computed: {
        getAgencyName() {
            if (!this.delete_id) {
                return "";
            }

            const agency = this.items.length
                ? this.items.find((i) => i.id === this.delete_id)
                : [];
            if (agency) {
                if (!agency.name.sync_with_quickbooks) {
                    return `Are you sure you want to delete ${agency.name.name}?`;
                } else {
                    return `Are you sure you want to delete ${agency.name.name}? Please note that you will also need to delete <Agency name> from Quickbooks manually. You can find instructions on how to do this <a href="https://quickbooks.intuit.com/learn-support/en-us/help-article/list-management/edit-delete-restore-list-elements/L0dql1u1h_US_en_US">here</a>`;
                }
            }

            return "";
        },
        // Status options normalised for AgenciesHeader segmented control.
        // Backend gives `{id, label}`; the new design optionally renders a
        // count — we don't have per-status counts on the backend yet, so
        // leave count undefined and the chip just shows the label.
        formattedStatusOptions() {
            const list = this.options.status || [];
            return list.map((s) => ({
                id: s.id,
                label: s.label,
                count: s.count != null ? s.count : null,
            }));
        },
        // `exportHref` is now a prop hydrated from the blade (admin-only).
        // The previously-hardcoded "/admin/agencies/export" pointed at a
        // route that never existed — the real route is named
        // `admin.agencies.export` and lives under /admin-panel/...

        // Facet chips for the header — Service / Industry / Source / Created.
        // Each chip shows a brief value summary derived from the current
        // filter state; clicking the chip opens the FilterRail sheet.
        facetChips() {
            const summarizeMulti = (value) => {
                if (!Array.isArray(value) || value.length === 0) return "Any";
                if (value.length === 1) return "1 selected";
                return `${value.length} selected`;
            };
            const summarizeSingle = (value) => {
                if (value == null || value === "") return "Any";
                return String(value).length > 12 ? "1 selected" : String(value);
            };
            return [
                {
                    slug: "service",
                    label: "Service",
                    value: summarizeMulti(this.filters.service),
                    active: Array.isArray(this.filters.service) && this.filters.service.length > 0,
                },
                {
                    slug: "industry",
                    label: "Industry",
                    value: summarizeMulti(this.filters.industry),
                    active: Array.isArray(this.filters.industry) && this.filters.industry.length > 0,
                },
                {
                    slug: "source",
                    label: "Source",
                    value: summarizeSingle(this.filters.source),
                    active: this.filters.source != null && this.filters.source !== "",
                },
                {
                    slug: "type",
                    label: "Type",
                    value: summarizeSingle(this.filters.type),
                    active: this.filters.type != null && this.filters.type !== "",
                },
            ];
        },
        // Number of active filters across the rail — drives the "9" badge
        // next to "More filters". Counts each set value of: niche, skill,
        // language, client_size, international, type, source, plus
        // service+industry multi-selects + the QB name + min_budget.
        activeFilterCount() {
            const f = this.filters || {};
            let n = 0;
            if (Array.isArray(f.service) && f.service.length) n++;
            if (Array.isArray(f.industry) && f.industry.length) n++;
            if (f.name_in_quickbooks) n++;
            if (f.client_size != null && f.client_size !== "") n++;
            if (f.international != null) n++;
            if (f.type != null && f.type !== "") n++;
            if (f.language != null && f.language !== "") n++;
            if (f.niche != null && f.niche !== "") n++;
            if (f.skill != null && f.skill !== "") n++;
            if (f.source != null && f.source !== "") n++;
            if (f.min_budget) n++;
            return n;
        },
        // ===== Page-level chrome styles =====
        appStyle() {
            return {
                height: "100vh",
                background: "var(--bg)",
                color: "var(--text)",
                fontFamily: "var(--font-sans)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            };
        },
        bodyStyle() {
            return {
                display: "flex",
                flex: "1 1 auto",
                minHeight: 0,
                flexDirection: "row",
            };
        },
        mainStyle() {
            return {
                flex: "1 1 auto",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                background: "var(--bg)",
            };
        },
        loaderWrapStyle() {
            return {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "60px",
                color: "var(--text-muted)",
            };
        },
    },

    methods: {
        statusBadgeClass(status) {
            const normalizedStatus = String(status || "")
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-|-$/g, "");

            return normalizedStatus
                ? `agency-status-badge--${normalizedStatus}`
                : "agency-status-badge--empty";
        },

        // ===== New-design event handlers (thin wrappers over legacy fns) =====
        onFiltersChanged(next) {
            this.filters = { ...this.filters, ...next };
            this.debouncedFetch();
        },
        onSearchInput(value) {
            this.searchRequest = value;
            this.debouncedFetch();
        },
        onStatusClick(id) {
            // null = "All" tab → wipe the multi-selection.
            if (id == null) {
                this.filters.agencies_status = [];
                this.fetchData();
                return;
            }
            // Multi-select toggle: clicking the same chip removes it,
            // clicking a new chip adds it. This matches the original
            // checkbox UX through the new segmented control.
            const idStr = String(id);
            const current = this.filters.agencies_status.map((v) => String(v));
            const next = current.includes(idStr)
                ? current.filter((v) => v !== idStr)
                : [...current, idStr];
            this.filters.agencies_status = next;
            this.fetchData();
        },
        // Variant A: open the FilterRail sheet on the right. Clicking
        // any facet chip routes through here as well — feels heavier
        // than an inline popover but ensures the user can edit the
        // facet using the same FilterRail controls.
        toggleFilters() {
            this.showFilters = !this.showFilters;
        },
        // Mobile filter-sheet emits `{ key, value }` per toggle. We mutate
        // the same filters object the desktop FilterRail uses so the
        // backend contract is identical.
        onMobileFilterUpdate({ key, value }) {
            if (!key) return;
            this.filters = { ...this.filters, [key]: value };
            this.debouncedFetch();
        },
        // Inline X on an active facet chip — wipe that one filter without
        // opening the sheet. The slugs mirror the facetChips computed.
        onClearFacet(slug) {
            if (!slug) return;
            const next = { ...this.filters };
            if (slug === "service" || slug === "industry") {
                next[slug] = [];
            } else {
                next[slug] = null;
            }
            this.filters = next;
            this.fetchData();
        },

        deleteAgency(id) {
            this.loading = true;

            axios
                .post("/agencies/delete", {
                    id: id,
                })
                .then((res) => {
                    this.loading = false;
                    if (res.data.isSuccess) {
                        this.fetchData();
                        this.delete_id = null;
                    } else if (res.data.redirect) {
                        location.href = res.data.redirect;
                    }
                })
                .catch((err) => {
                    console.error("Delete agency error", err);
                    this.loading = false;
                });
        },

        onChangeFilterItem(value) {
            if (!this.loading) {
                this.loading = true;
                setTimeout(() => {
                    this.fetchData();
                }, 150);
            }
        },

        fetchData() {
            this.loading = true;

            axios
                .post("/agencies/getList", {
                    filters: {
                        ...this.filters,
                        search: this.searchRequest,
                    },
                })
                .then((res) => {
                    if (res.data.length) {
                        this.items = this.reformatDataByColumns(res.data);
                    } else {
                        this.items = [];
                    }
                })
                .catch((err) => {})
                .finally(() => {
                    this.loading = false;
                });
        },

        search(value, e) {
            this.searchRequest = value;
            this.fetchData();
        },

        sortData(sortBy) {
            let self = this;
            if (self.lastSort !== sortBy) self.descending = false;
            self.lastSort = sortBy;
            switch (sortBy) {
                case "id":
                    self.items = self.items.sort(sortNumber("id", self.descending));
                    break;
                case "name":
                    self.items = self.items.sort(
                        sortText("name", self.descending, (a) => a.name.toUpperCase())
                    );
                    break;
                case "status":
                    self.items = self.items.sort(sortText("status", self.descending));
                    break;
                case "state":
                    self.items = self.items.sort(sortText("state", self.descending));
                    break;
                case "source":
                    self.items = self.items.sort(sortText("source", self.descending));
                    break;
                case "active_deals":
                    self.items = self.items.sort(sortNumber("active_deals", self.descending));
                    break;
                case "ytd_deals":
                    self.items = self.items.sort(sortNumber("ytd_deals", self.descending));
                    break;
                case "total_deals":
                    self.items = self.items.sort(sortNumber("total_deals", self.descending));
                    break;
                case "ytd_sales_amount":
                    self.items = self.items.sort(sortMoney("ytd_sales_amount", self.descending));
                    break;
                case "ytd_commissions":
                    self.items = self.items.sort(sortMoney("ytd_commissions", self.descending));
                    break;
                case "current_invoice":
                    self.items = self.items.sort(sortMoney("current_invoice", self.descending));
                    break;
                case "total_invoice":
                    self.items = self.items.sort(sortMoney("total_invoice", self.descending));
                    break;
                case "current_commissions":
                    self.items = self.items.sort(sortMoney("current_commissions", self.descending));
                    break;
                case "total_commissions":
                    self.items = self.items.sort(sortMoney("total_commissions", self.descending));
                    break;
                case "created_at":
                    self.items = self.items.sort(sortDate("created_at", self.descending));
                    break;
            }
            self.descending = !self.descending;
        },

        // MODAL COLUMNS
        reformatDataByColumns(__items = []) {
            try {
                if (__items.length >= 1) {
                    const newFormatData = [];
                    if (this.current_columns.length >= 1) {
                        __items.forEach((item) => {
                            const tempObj = {};
                            this.current_columns
                                .sort((a, b) => a.position - b.position)
                                .forEach((c) => {
                                    tempObj[`${c.slug}`] = item[c.slug];

                                    if (c.slug === "name") {
                                        tempObj["name"] = {
                                            name: item["name"],
                                            link: item["agency"].url,
                                            favicon: item["favicon"],
                                            sync_with_quickbooks:
                                                item["agency"].sync_with_quickbooks,
                                        };
                                    }
                                });

                            // Carry id at top level so the table can render
                            // the #0001 badge + edit/delete links without
                            // walking into nested `name.id`.
                            if (item.id != null) tempObj.id = item.id;
                            else if (item.agency && item.agency.id != null) tempObj.id = item.agency.id;

                            newFormatData.push(tempObj);
                        });

                        return newFormatData;
                    }
                }

                return [];
            } catch (err) {
                console.error("reformatDataByColumns error", err);
            }
        },
        openColumnsModal() {
            this.showModalColumns = true;
            document.querySelector("html").style = "overflow-y: hidden";
            document.querySelector("body").style =
                "overflow-y: hidden; margin-right: 16px";
        },
        async closeModal(items = []) {
            await axios
                .post("/agencies/updateColumns", {
                    columns: items,
                })
                .then(() => {
                    this.showModalColumns = false;
                    this.current_columns.length = 0;
                    this.current_columns = JSON.parse(JSON.stringify(items));
                    document.querySelector("html").style = "";
                    document.querySelector("body").style = "";
                    this.reformatDataByColumns();
                })
                .catch((err) => {
                    console.error("Axios update columns error", err);
                });
        },
        selectColumn(col) {
            this.selected_col = col;
        },

        toggleSidebar() {
            this.is_open_sidebar = !this.is_open_sidebar;
        },

        clearFilter() {
            (this.searchRequest = ""),
                (this.filters = {
                    industry: null,
                    service: null,
                    name_in_quickbooks: "",
                    client_size: null,
                    international: null,
                    type: null,
                    language: null,
                    niche: null,
                    skill: null,
                    source: null,
                    min_budget: "",
                    status: null,
                    agencies_status: [],
                    budget_type: "monthly",
                    state: null,
                });
            this.fetchData();
        },

        handleDeleteAgency(id) {
            this.delete_id = id;
        },
    },
};
</script>
